import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, Gift, Star, BookOpen, ClipboardList, FileSignature, Activity, HeartHandshake, MessageSquareWarning, CalendarCheck, Baby, Radiation, FileEdit, HelpCircle, NotebookPen } from 'lucide-react';
export function ProductDetails() {
  const tags = ['Guiones de consulta', 'Consentimientos', 'Plantillas por tumor', 'Semáforo de síntomas', 'Efectos adversos', 'Malas noticias', 'Pronóstico', 'Pacto de silencio', 'Vida cotidiana', 'Seguimiento post-RT', 'Pediatría', 'Braquiterapia'];
  const skills = ['Explicación del plan de tratamiento', 'Consentimiento en lenguaje llano', 'Anticipación de toxicidades', 'Comunicación de malas noticias (SPIKES)', 'Respuesta a la pregunta del pronóstico', 'Manejo del pacto de silencio', 'Educación del paciente y la familia', 'Consulta de alta estructurada', 'Comunicación pediátrica por edad', 'Explicación de técnicas de alta precisión'];
  const tomos = [{
    id: 'T1',
    Icon: ClipboardList,
    title: 'Cómo Explicar el Plan de Tratamiento Sin Que el Paciente Se Pierda',
    subtitle: 'La consulta de 40 minutos, resuelta en 20',
    content: 'Guión de primera consulta en 6 pasos, plantillas de explicación por sitio tumoral (mama, próstata, cérvix, pulmón, recto, cabeza y cuello, SNC) y checklist de comprensión. El paciente sale entendiendo — y vos salís a horario.',
    bullets: ['Guión de primera consulta paso a paso con frases exactas', '7 plantillas de explicación por tipo de tumor', 'Hoja entregable: "Lo que va a pasar desde hoy hasta el último día"']
  }, {
    id: 'T2',
    Icon: FileSignature,
    title: 'Consentimientos Informados en Lenguaje de Paciente',
    subtitle: 'De documento defensivo a herramienta de confianza',
    content: '7 consentimientos completos en lenguaje llano, compatibles con la Ley 26.529 y el Código Civil argentino. El paciente los lee en sala de espera y llega a la firma con preguntas reales, no con miedo.'
  }, {
    id: 'T3',
    Icon: Activity,
    title: 'Efectos Adversos: Cómo Anticiparlos y Cómo Explicarlos',
    subtitle: 'El semáforo que reduce las llamadas fuera de horario a la mitad',
    content: ''
  }, {
    id: 'T4',
    Icon: HeartHandshake,
    title: 'La Vida del Paciente Fuera del Consultorio',
    subtitle: 'Trabajo, mate, sexualidad, licencias y obras sociales — las respuestas que nadie tiene preparadas',
    content: ''
  }, {
    id: 'T5',
    Icon: MessageSquareWarning,
    title: 'Comunicación de Malas Noticias en Oncología Radioterápica',
    subtitle: 'SPIKES adaptado a Argentina, la pregunta del pronóstico y el pacto de silencio — con guión completo anotado',
    content: ''
  }, {
    id: 'T6',
    Icon: CalendarCheck,
    title: 'Seguimiento Post-RT: el Alta y los Controles',
    subtitle: 'La consulta de alta de 15 minutos y cómo explicar la respuesta que tarda meses',
    content: ''
  }, {
    id: 'T7',
    Icon: Baby,
    title: 'Radioterapia Pediátrica: el Niño y su Familia',
    subtitle: 'Guiones palabra por palabra por edad, la máscara como casco de superhéroe y la consulta con los padres',
    content: '',
    exclusive: true
  }, {
    id: 'T8',
    Icon: Radiation,
    title: 'Braquiterapia y Técnicas Avanzadas',
    subtitle: 'El banco de analogías que explica lo invisible: semillas, radiocirugía y SBRT sin generar terror',
    content: '',
    exclusive: true
  }];
  return <div className="w-full" data-id="element-195">
      {/* System Overview Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-50" data-id="element-196">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-197">
          <div className="text-center mb-8 sm:mb-12" data-id="element-198">
            <span className="text-teal-600 font-bold tracking-wider uppercase text-xs sm:text-sm mb-2 block" data-id="element-199">
              SISTEMA N°1 EN ARGENTINA
            </span>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 sm:mb-6" data-id="element-200">
              8 Tomos + 3 Bonos · Todo resuelto
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 font-medium mb-6 sm:mb-8" data-id="element-201">
              <span className="text-teal-600 font-bold" data-id="element-202">+50</span> herramientas
              aplicables · <span className="text-teal-600 font-bold" data-id="element-203">+80</span>{' '}
              frases clínicas listas para usar
            </p>

            <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-10" data-id="element-204">
              {tags.map((tag, idx) => <span key={idx} className="bg-white border border-slate-200 text-slate-600 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium shadow-sm" data-id="element-205">
                  {tag}
                </span>)}
            </div>

            <p className="text-base sm:text-lg text-slate-800 font-semibold bg-teal-50 inline-block px-4 sm:px-6 py-2 sm:py-3 rounded-lg border border-teal-100" data-id="element-206">
              Funciona desde hoy en tu consultorio, en tu tablet o impreso en
              papel.
            </p>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-slate-100" data-id="element-207">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2" data-id="element-208">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500" data-id="element-209" />{' '}
              Habilidades incluidas:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4" data-id="element-210">
              {skills.map((skill, idx) => <div key={idx} className="flex items-start gap-2 sm:gap-3" data-id="element-211">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500 shrink-0 mt-0.5" data-id="element-212" />
                  <span className="text-sm sm:text-base text-slate-700" data-id="element-213">
                    {skill}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 8 Tomos Accordion Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white" data-id="element-214">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-215">
          <div className="text-center mb-8 sm:mb-12" data-id="element-216">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 sm:mb-4" data-id="element-217">
              LOS 8 TOMOS
            </h2>
            <p className="text-lg sm:text-xl text-slate-600" data-id="element-218">
              Tu hoja de ruta para comunicar con seguridad en cada etapa del
              tratamiento
            </p>
            <p className="text-xs sm:text-sm text-teal-600 font-medium mt-4 sm:mt-6" data-id="element-219">
              Tocá cada tomo para ver lo que incluye 👆
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 items-start" data-id="element-220">
            {tomos.map(tomo => <AccordionItem key={tomo.id} tomo={tomo} data-id="element-221" />)}
          </div>
        </div>
      </section>

      {/* 3 Bonos Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-222">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 sm:w-96 sm:h-96 bg-amber-500 rounded-full blur-3xl opacity-10" data-id="element-223"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-224">
          <div className="text-center mb-8 sm:mb-12" data-id="element-225">
            <span className="inline-block py-1 px-3 rounded-full bg-amber-500/20 text-amber-400 text-xs sm:text-sm font-bold tracking-wide border border-amber-500/30 mb-3 sm:mb-4" data-id="element-226">
              INCLUIDOS GRATIS
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-3 sm:mb-4" data-id="element-227">
              3 Bonos de Alto Valor Clínico
            </h2>
            <p className="text-lg sm:text-xl text-slate-300" data-id="element-228">
              Sin costo extra — incluidos en todas las ofertas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-229">
            <BonusCard id="B1" Icon={FileEdit} title="Plantillas Editables de Consentimiento en Word y PDF" desc="Descargá, poné el membrete de tu institución y usalas hoy. 7 consentimientos listos para imprimir." delay={0} data-id="element-230" />
            <BonusCard id="B2" Icon={HelpCircle} title="Guía de Respuestas a las 30 Preguntas Más Frecuentes del Paciente" desc={'"¿Quedo radioactivo?", "¿el azúcar alimenta el tumor?", "vi sangre, ¿avanzó?" — respuestas de 15 segundos, listas para usar y para entrenar a tu equipo.'} delay={0.1} data-id="element-231" />
            <BonusCard id="B3" Icon={NotebookPen} title="Sistema de Seguimiento Semanal del Paciente" desc="El cuadernillo imprimible que reemplaza 10 minutos de anamnesis desestructurada por 60 segundos de lectura. Con variantes para pelvis y cabeza y cuello." delay={0.2} data-id="element-232" />
          </div>

          <div className="text-center" data-id="element-233">
            <motion.div whileHover={{
            scale: 1.02
          }} className="inline-block bg-gradient-to-r from-amber-600 to-amber-500 p-[2px] rounded-xl cursor-default" data-id="element-234">
              <div className="bg-slate-900 px-6 sm:px-8 py-3 sm:py-4 rounded-[10px]" data-id="element-235">
                <p className="text-lg sm:text-2xl font-extrabold text-amber-400" data-id="element-236">
                  VALOR TOTAL DE LOS BONOS: $42.000 — GRATIS
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}
function AccordionItem({
  tomo
}: {
  tomo: any;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = tomo.Icon;
  return <div className={`relative border rounded-xl overflow-hidden transition-colors ${isOpen ? 'border-teal-500 bg-teal-50/30' : 'border-slate-200 bg-white'}`} data-id="element-237">
      <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${isOpen ? 'bg-teal-500' : 'bg-transparent'}`} data-id="element-238" />
      <motion.button whileTap={{
      scale: 0.99
    }} onClick={() => setIsOpen(!isOpen)} className="w-full px-3.5 py-3.5 sm:px-4 sm:py-4 flex items-start justify-between text-left focus:outline-none gap-3" aria-expanded={isOpen} data-id="element-239">
        <div className="flex items-start gap-3 min-w-0" data-id="element-240">
          <div className={`shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-241">
            <Icon className="w-4.5 h-4.5 sm:w-5 sm:h-5" data-id="element-242" />
          </div>
          <div className="min-w-0" data-id="element-243">
            <div className="flex items-center gap-2 mb-0.5" data-id="element-244">
              <span className={`font-bold text-xs sm:text-sm shrink-0 ${isOpen ? 'text-teal-600' : 'text-slate-400'}`} data-id="element-245">
                {tomo.id}
              </span>
              {tomo.exclusive && <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full border border-amber-200" data-id="element-246">
                  <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" data-id="element-247" />{' '}
                  Exclusivo Megapack
                </span>}
            </div>
            <h3 className="font-bold text-slate-900 text-sm sm:text-base leading-tight" data-id="element-248">
              {tomo.title}
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5 leading-snug" data-id="element-249">
              {tomo.subtitle}
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 shrink-0 text-slate-400 transition-transform duration-300 mt-1 ${isOpen ? 'rotate-180 text-teal-600' : ''}`} data-id="element-250" />
      </motion.button>

      <AnimatePresence data-id="element-251">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-252">
            <div className="px-3.5 sm:px-4 pb-4 pt-1 pl-[3.25rem] sm:pl-[3.75rem]" data-id="element-253">
              {tomo.content && <p className="text-sm text-slate-700 mb-3" data-id="element-254">{tomo.content}</p>}
              {tomo.bullets && <ul className="space-y-2" data-id="element-255">
                  {tomo.bullets.map((bullet: string, i: number) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-256">
                      <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" data-id="element-257" />
                      <span data-id="element-258">{bullet}</span>
                    </li>)}
                </ul>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function BonusCard({
  id,
  Icon,
  title,
  desc,
  delay = 0
}: {
  id: string;
  Icon: any;
  title: string;
  desc: string;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    delay
  }} whileHover={{
    y: -5
  }} whileTap={{
    scale: 0.98
  }} className="bg-slate-800 border border-slate-700 rounded-2xl p-5 sm:p-6 relative cursor-pointer transition-shadow hover:shadow-lg hover:shadow-amber-900/10" data-id="element-259">
      <div className="absolute -top-3 -right-3 bg-amber-500 text-slate-900 font-bold px-2.5 py-1 rounded-full text-xs shadow-lg transform rotate-3 flex items-center gap-1" data-id="element-260">
        <Gift className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-261" /> Gratis
      </div>
      <div className="flex items-center gap-3 mb-3" data-id="element-262">
        <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400" data-id="element-263">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-264" />
        </div>
        <span className="text-amber-400 font-bold text-lg sm:text-xl" data-id="element-265">
          {id}
        </span>
      </div>
      <h3 className="text-base sm:text-lg font-bold text-white mb-2 leading-snug" data-id="element-266">
        {title}
      </h3>
      <p className="text-sm text-slate-300 leading-relaxed" data-id="element-267">{desc}</p>
    </motion.div>;
}